import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { useDispatch, useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import api from 'api';
import { ModalButtonWithForm } from 'components/common';
import { FormGroup, Text } from 'components/common/form';

import { useFiltersContext } from './FilterContext';

interface Props {
  className?: string;
}

/**
 * Button component that opens a modal to save the current filter configuration
 * It only takes into account filters defined by the Filters component
 *
 * @param className - Optional CSS class name
 */
const SaveFiltersButton = ({ className }: Props) => {
  const router = useRouter();

  const { dispatch } = useDispatch();

  const { messages } = useReduxState();

  const { filters, savedFilters, savedFiltersUpdate } = useFiltersContext();

  const validFilters = useMemo(
    () =>
      Object.keys(filters).reduce((acc, filter) => {
        filters[filter].forEach(({ field, filters }) => {
          if (filters) {
            filters.forEach(({ field }) => {
              acc.add(field);
            });
          } else acc.add(field);
        });
        return acc;
      }, new Set<string>()),
    [filters]
  );

  // Create URLSearchParams from current query
  const getCurrentFilterParams = () => {
    const filterQuery = new URLSearchParams();
    Object.entries(router.query).forEach(([key, value]) => {
      if (!validFilters.has(key)) return;
      if (value && typeof value === 'string') {
        filterQuery.append(key, value);
      }
    });

    return filterQuery;
  };

  const currentFilterParams = getCurrentFilterParams();
  const currentFilterString = currentFilterParams.toString();

  const matchingSavedFilter = savedFilters.find(
    (filter) => filter.filterQuery === currentFilterString
  );
  const hasActiveFilters = currentFilterString !== '' && !matchingSavedFilter;

  const alreadySavedTooltip =
    !hasActiveFilters && matchingSavedFilter
      ? getMessageWithPlaceholders({
          message: messages.savedFiltersAlreadySaved,
          placeholders: { name: matchingSavedFilter.name }
        })
      : undefined;

  const handleSubmit = async (values: { name?: string }) => {
    if (!values || !values.name) {
      return false;
    }
    if (currentFilterString === '') {
      return false;
    }

    // Use router.pathname which will contain the Next.js route path
    // For dynamic routes this will be something like "/admin/courses/[courseSlug]/users"
    // instead of the actual path like "/admin/courses/react-basics/users"
    const pagePath = router.pathname;

    const result = await saveFilter(
      values.name.trim(),
      currentFilterString,
      pagePath
    );
    if (result.success) {
      dispatch.addNotice({
        text: messages.savedFiltersAddSuccess,
        type: 'success'
      });
      return true;
    }
  };

  /**
   * Saves a filter configuration to the database
   *
   * @param name - The name to give to this saved filter
   * @param filterQuery - The query string representing the filter configuration
   * @param pagePath - The path of the page where this filter is used
   * @returns Object with success status
   */
  const saveFilter = async (
    name: string,
    filterQuery: string,
    pagePath: string
  ) => {
    const response = await api.post('/admin/saved-filters', {
      name: name.trim(),
      filterQuery,
      pagePath
    });
    if (response.data?.success) {
      await savedFiltersUpdate();
      return { success: true };
    }

    return { success: false };
  };

  return (
    <ModalButtonWithForm
      buttonProps={{
        className,
        category: 'success',
        size: 'tiny',
        tooltip: alreadySavedTooltip || undefined,
        disabled: !hasActiveFilters,
        text: messages.commonSave,
        style: { padding: '4px 6px 5px', position: 'relative', top: 1 },
        icon: 'check'
      }}
      buttonCategory="success"
      modalTitle={messages.savedFiltersModalTitle}
      modalWidth="small"
      modalActionText={messages.commonSave}
      onSubmit={handleSubmit}
      messages={messages}
    >
      {() => (
        <div className="save-filter-modal">
          <FormGroup htmlFor="name" label={messages.savedFiltersName}>
            <Text field="name" id="name" maxLength={255} required />
          </FormGroup>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default SaveFiltersButton;
